/**
 * Client branding — dynamically sets browser tab title and favicons.
 *
 * Controlled via two environment / runtime-config variables:
 *   VITE_APP_TITLE    – browser tab title (default: "cdbOS")
 *   VITE_APP_BRANDING – subfolder name under /assets/branding/ containing
 *                        the client's icon set (e.g. "siemens").
 *                        When empty the default /assets/ icons are used.
 *
 * Expected icon files per branding folder:
 *   favicon.ico, favicon-16x16.png, favicon-32x32.png,
 *   apple-touch-icon.png, android-chrome-192x192.png, android-chrome-512x512.png
 */

import { env } from '@/lib/env'

export function applyBranding(): void {
  // ── Tab title ──────────────────────────────────────────────────────────────
  document.title = env.appTitle

  // ── Branding attribute on <html> (enables CSS theme overrides) ────────────
  const branding = env.appBranding
  if (branding) {
    document.documentElement.setAttribute('data-branding', branding)
  } else {
    document.documentElement.removeAttribute('data-branding')
  }

  if (!branding) return // keep default /assets/ icons

  // ── Favicons ───────────────────────────────────────────────────────────────

  const base = `/assets/branding/${branding}`

  const iconMap: Record<string, string> = {
    'link[rel="icon"]:not([type]):not([sizes])':       `${base}/favicon.ico`,
    'link[rel="icon"][sizes="32x32"]':                 `${base}/favicon-32x32.png`,
    'link[rel="icon"][sizes="16x16"]':                 `${base}/favicon-16x16.png`,
    'link[rel="apple-touch-icon"]':                    `${base}/apple-touch-icon.png`,
  }

  for (const [selector, href] of Object.entries(iconMap)) {
    const el = document.querySelector<HTMLLinkElement>(selector)
    if (el) el.href = href
  }

  // ── Web App Manifest ─────────────────────────────────────────────────────
  // Dynamically replace the static manifest with a blob containing the
  // branding-specific icons and title so PWA installs use the right assets.
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (manifestLink) {
    const manifest = {
      name: env.appTitle,
      short_name: env.appTitle,
      icons: [
        { src: `${base}/android-chrome-192x192.png`, sizes: '192x192', type: 'image/png' },
        { src: `${base}/android-chrome-512x512.png`, sizes: '512x512', type: 'image/png' },
      ],
      theme_color: '#1E88E5',
      background_color: '#1E88E5',
      display: 'standalone' as const,
    }
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' })
    manifestLink.href = URL.createObjectURL(blob)
  }
}
